import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {

  Button,

  Card,

  Form,

  Input,

  InputNumber,

  message,

  Select,

  Space,

  Switch,

  Table,

  Tabs,

  Tag,

  Typography,

  Modal,

} from 'antd';

import { ApiOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import { connectorsService } from '../services/connectorsService';

import { accountService } from '../services/accountService';

import { INCIDENT_CLIENT_ID } from '../constants/incidentConnectors';

import {
  AUTH_WORKSPACE_API_KEY,
  GRAFANA_AUTH_TYPE_OPTIONS,
  GRAFANA_BASE_URL_HINT,
  GRAFANA_PLACEHOLDERS,
  credentialLabelFor,
  shouldShowGrafanaConfigField,
  shouldShowGrafanaTokenField,
} from '../constants/grafanaConnectorHints';

import { useTheme } from '../context/ThemeContext';

import { useAuth } from '../context/AuthContext';



const { Title, Text } = Typography;

const { TabPane } = Tabs;



const flattenAccountsResponse = (data) => {

  if (Array.isArray(data)) return data;

  if (!data || typeof data !== 'object') return [];

  const mapped = [];

  for (const cid of Object.keys(data)) {

    for (const accountId of Object.keys(data[cid] || {})) {

      mapped.push({

        ...data[cid][accountId],

        accountId,

        clientId: cid,

      });

    }

  }

  return mapped;

};



const mergeConnectorLists = (...lists) => {

  const byId = new Map();

  for (const list of lists) {

    for (const item of list || []) {

      if (item?.connector_id) byId.set(item.connector_id, item);

    }

  }

  return Array.from(byId.values());

};



const ConnectorsPage = () => {

  const { isDark } = useTheme();

  const { user, accounts, setAccounts } = useAuth();

  const observabilityClientId = useMemo(() => {

    const alertAccount = accounts?.[0];

    return (alertAccount ? (alertAccount.clientId || user?.client_id) : null) || 'default';

  }, [accounts, user?.client_id]);



  const [types, setTypes] = useState([]);

  const [instances, setInstances] = useState([]);

  const [repoMappings, setRepoMappings] = useState([]);

  const [loading, setLoading] = useState(false);

  const [importing, setImporting] = useState(false);

  const [form] = Form.useForm();

  const [mcpForm] = Form.useForm();

  const [mappingForm] = Form.useForm();

  const [editForm] = Form.useForm();

  const [selectedType, setSelectedType] = useState('loki');

  const [editModalOpen, setEditModalOpen] = useState(false);

  const [editingRecord, setEditingRecord] = useState(null);

  const [editSaving, setEditSaving] = useState(false);



  const clientIdForRecord = useCallback(

    (record) => (record?.category === 'action' ? INCIDENT_CLIENT_ID : observabilityClientId),

    [observabilityClientId],

  );



  const clientIdForConnectorType = useCallback(

    (connectorType) => {

      const def = types.find((t) => t.type === connectorType);

      return def?.category === 'action' ? INCIDENT_CLIENT_ID : observabilityClientId;

    },

    [types, observabilityClientId],

  );



  const load = useCallback(async () => {

    setLoading(true);

    try {

      const [typeList, obsInstances, actionInstances, maps] = await Promise.all([

        connectorsService.getTypes(),

        connectorsService.listInstances({}, observabilityClientId),

        connectorsService.listInstances({ category: 'action' }, INCIDENT_CLIENT_ID),

        connectorsService.listRepoMappings(INCIDENT_CLIENT_ID),

      ]);

      setTypes(typeList);

      setInstances(mergeConnectorLists(obsInstances, actionInstances));

      setRepoMappings(maps);

    } catch (e) {

      message.error(e.message || 'Failed to load connectors');

    } finally {

      setLoading(false);

    }

  }, [observabilityClientId]);



  useEffect(() => {

    if (accounts?.length > 0) return;

    accountService

      .getAccounts()

      .then((data) => {

        const mapped = flattenAccountsResponse(data);

        if (mapped.length > 0) setAccounts(mapped);

      })

      .catch(() => {});

  }, [accounts?.length, setAccounts]);



  useEffect(() => {

    load();

  }, [load]);



  const datasourceTypes = types.filter((t) => t.category === 'datasource');

  const actionTypes = types.filter((t) => t.category === 'action');

  const mcpInstances = instances.filter((i) => i.category === 'agent' && i.type === 'mcp');



  const handleCreate = async (values, typeOverride) => {

    const connectorType = typeOverride || selectedType;

    const targetClientId = clientIdForConnectorType(connectorType);

    try {

      const { name, enabled, is_default, agent_enabled, token, username, password, ...config } = values;

      const credentials = {};

      if (token) credentials.token = token;

      if (username) credentials.username = username;

      if (password) credentials.password = password;



      await connectorsService.createInstance({

        type: connectorType,

        name,

        config,

        enabled: enabled !== false,

        is_default: Boolean(is_default),

        agent_enabled: agent_enabled !== false,

        credentials: Object.keys(credentials).length ? credentials : undefined,

      }, targetClientId);

      message.success('Connector created');

      if (connectorType === 'mcp') mcpForm.resetFields();

      else form.resetFields();

      load();

    } catch (e) {

      message.error(e.message || 'Create failed');

    }

  };



  const handleImportEnvMcp = async () => {

    setImporting(true);

    try {

      const result = await connectorsService.importEnvMcp(observabilityClientId);

      message.success(`Imported ${result.created} MCP connector(s) from environment defaults`);

      load();

    } catch (e) {

      message.error(e.message || 'Import failed');

    } finally {

      setImporting(false);

    }

  };



  const handleTest = async (connectorId, tenantClientId) => {

    try {

      const result = await connectorsService.testInstance(connectorId, tenantClientId);

      if (result.success) message.success(result.message);

      else message.error(result.message);

    } catch (e) {

      message.error(e.message || 'Test failed');

    }

  };



  const handleToggle = async (record, enabled) => {

    try {

      await connectorsService.updateInstance(

        record.connector_id,

        { enabled },

        clientIdForRecord(record),

      );

      load();

    } catch (e) {

      message.error('Update failed');

    }

  };



  const handleToggleAgent = async (record, agentEnabled) => {

    try {

      await connectorsService.updateInstance(

        record.connector_id,

        { agent_enabled: agentEnabled },

        clientIdForRecord(record),

      );

      load();

    } catch (e) {

      message.error('Update failed');

    }

  };



  const handleDelete = async (record) => {

    try {

      await connectorsService.deleteInstance(record.connector_id, clientIdForRecord(record));

      message.success('Deleted');

      load();

    } catch (e) {

      message.error('Delete failed');

    }

  };



  const handleMapping = async (values) => {

    try {

      await connectorsService.createRepoMapping(values, INCIDENT_CLIENT_ID);

      mappingForm.resetFields();

      load();

      message.success('Repo mapping saved');

    } catch (e) {

      message.error(e.message || 'Failed to save mapping');

    }

  };



  const openEditModal = (record) => {

    setEditingRecord(record);

    editForm.setFieldsValue({

      name: record.name,

      enabled: record.enabled,

      is_default: Boolean(record.is_default),

      agent_enabled: record.agent_enabled !== false,

      ...(record.config || {}),

    });

    setEditModalOpen(true);

  };



  const closeEditModal = () => {

    setEditModalOpen(false);

    setEditingRecord(null);

    editForm.resetFields();

  };



  const handleEditSubmit = async (values) => {

    if (!editingRecord) return;

    setEditSaving(true);

    try {

      const {

        name,

        enabled,

        is_default,

        agent_enabled,

        token,

        username,

        password,

        ...config

      } = values;

      const payload = {

        name,

        enabled: enabled !== false,

        config,

      };

      if (editingRecord.category === 'datasource') {

        payload.is_default = Boolean(is_default);

      }

      if (editingRecord.type === 'mcp') {

        payload.agent_enabled = agent_enabled !== false;

      }

      const credentials = {};

      if (token) credentials.token = token;

      if (username) credentials.username = username;

      if (password) credentials.password = password;

      if (Object.keys(credentials).length) {

        payload.credentials = credentials;

      }

      await connectorsService.updateInstance(

        editingRecord.connector_id,

        payload,

        clientIdForRecord(editingRecord),

      );

      message.success('Connector updated');

      closeEditModal();

      load();

    } catch (e) {

      message.error(e.message || 'Update failed');

    } finally {

      setEditSaving(false);

    }

  };



  const columns = [

    { title: 'Name', dataIndex: 'name', key: 'name' },

    { title: 'Type', dataIndex: 'type', key: 'type', render: (t) => <Tag>{t}</Tag> },

    { title: 'Category', dataIndex: 'category', key: 'category' },

    {

      title: 'Enabled',

      dataIndex: 'enabled',

      key: 'enabled',

      render: (v, r) => <Switch checked={v} onChange={(checked) => handleToggle(r, checked)} />,

    },

    {

      title: 'Actions',

      key: 'actions',

      render: (_, r) => (

        <Space>

          <Button size="small" onClick={() => openEditModal(r)}>Edit</Button>

          <Button size="small" onClick={() => handleTest(r.connector_id, clientIdForRecord(r))}>Test</Button>

          <Button size="small" danger onClick={() => handleDelete(r)}>Delete</Button>

        </Space>

      ),

    },

  ];



  const mcpColumns = [

    ...columns.slice(0, 3),

    {

      title: 'Agent tools',

      key: 'tools',

      render: (_, r) => (

        r.last_test_tool_count != null

          ? `${r.last_test_tool_count} tool(s)`

          : <Text type="secondary">Run Test</Text>

      ),

    },

    {

      title: 'Agent enabled',

      dataIndex: 'agent_enabled',

      key: 'agent_enabled',

      render: (v, r) => (

        <Switch checked={v !== false} onChange={(checked) => handleToggleAgent(r, checked)} />

      ),

    },

    ...columns.slice(3),

  ];



  const createAuthType = Form.useWatch('auth_type', form);
  const editAuthType = Form.useWatch('auth_type', editForm);

  const renderTypeFields = (typeKey = selectedType, { formInstance } = {}) => {

    const def = types.find((t) => t.type === typeKey);

    if (!def) return null;

    const props = def.config_schema?.properties || {};

    const grafanaPlaceholders = GRAFANA_PLACEHOLDERS;
    const authType =
      typeKey === 'grafana'
        ? (formInstance === editForm ? editAuthType : createAuthType) || 'service_account_token'
        : undefined;

    return Object.entries(props).map(([key, schema]) => {
      if (typeKey === 'grafana' && !shouldShowGrafanaConfigField(key, authType)) {
        return null;
      }

      if (schema.type === 'boolean') {
        return (
          <Form.Item
            key={key}
            name={key}
            label={schema.title || key}
            valuePropName="checked"
            initialValue={schema.default !== undefined ? schema.default : true}
          >
            <Switch />
          </Form.Item>
        );
      }

      if (schema.type === 'integer') {

        return (

          <Form.Item key={key} name={key} label={schema.title || key} initialValue={schema.default}>

            <InputNumber style={{ width: '100%' }} />

          </Form.Item>

        );

      }

      if (schema.enum) {
        const options =
          typeKey === 'grafana' && key === 'auth_type'
            ? GRAFANA_AUTH_TYPE_OPTIONS
            : schema.enum.map((v) => ({ value: v, label: v }));

        return (

          <Form.Item
            key={key}
            name={key}
            label={schema.title || key}
            initialValue={schema.default}
            extra={
              typeKey === 'grafana' && key === 'auth_type'
                ? 'Manual: paste a Grafana service account token. Auto-refresh: ClairAI mints/renews an AMG workspace API key via AWS IAM.'
                : undefined
            }
          >

            <Select options={options} />

          </Form.Item>

        );

      }

      const placeholder =
        typeKey === 'grafana'
          ? grafanaPlaceholders[key]
          : schema.description || undefined;

      const required =
        typeKey === 'grafana' && key === 'workspace_id' && authType === AUTH_WORKSPACE_API_KEY;

      return (

        <Form.Item
          key={key}
          name={key}
          label={schema.title || key}
          initialValue={schema.default}
          rules={required ? [{ required: true, message: 'Workspace ID is required for auto-refresh' }] : undefined}
          extra={typeKey === 'grafana' && key === 'base_url' ? GRAFANA_BASE_URL_HINT : undefined}
        >

          <Input placeholder={placeholder} />

        </Form.Item>

      );

    });

  };



  const renderCredentialFields = (typeKey = selectedType, { editMode = false, formInstance } = {}) => {

    const def = types.find((t) => t.type === typeKey);

    if (!def?.credential_fields?.length) return null;

    const authType =
      typeKey === 'grafana'
        ? (formInstance === editForm ? editAuthType : createAuthType) || 'service_account_token'
        : undefined;

    if (typeKey === 'grafana' && !shouldShowGrafanaTokenField(authType)) {
      return (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Token is minted automatically via AWS <code>CreateWorkspaceApiKey</code> and stored in Secrets Manager.
          No service account token is required.
        </Text>
      );
    }

    return def.credential_fields.map((field) => {
      const isGrafanaToken = typeKey === 'grafana' && field === 'token';
      const label = credentialLabelFor(typeKey, field);
      const extraHints = [];
      if (editMode) extraHints.push('Leave blank to keep the current value');
      if (isGrafanaToken) {
        extraHints.push('Bearer service account token (never shown after save). Grafana cannot renew this token — create a new one when it expires.');
      }

      return (

      <Form.Item

        key={field}

        name={field}

        label={label}

        rules={
          isGrafanaToken && !editMode
            ? [{ required: true, message: 'Service account token is required' }]
            : undefined
        }

        extra={extraHints.length ? extraHints.join(' — ') : undefined}

      >

        <Input.Password autoComplete="new-password" placeholder={isGrafanaToken ? 'glsa_…' : undefined} />

      </Form.Item>

      );
    });

  };



  return (

    <div style={{ padding: isDark ? 0 : 0 }}>

      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>

        <Title level={4} style={{ margin: 0, color: isDark ? '#e6edf3' : '#262626' }}>

          <ApiOutlined /> Integrations & Connectors

        </Title>

        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>

      </Space>



      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>

        Configure observability datasources, AI Assistant MCP servers, and incident actions.

        With <code>CONNECTORS_ENABLED=true</code> and <code>CONNECTORS_PROXY_MODE=true</code>, the chatbot loads MCP tools from the AI Assistant tab via the backend proxy.

        <br />

        Tenant (client_id): <code>{INCIDENT_CLIENT_ID}</code> — connectors and incident actions are scoped to this tenant.

      </Text>



      <Tabs defaultActiveKey="observability">

        <TabPane tab="Observability" key="observability">

          <Card title="Configured connectors" style={{ marginBottom: 16 }}>

            <Table

              rowKey="connector_id"

              loading={loading}

              dataSource={instances.filter((i) => i.category === 'datasource')}

              columns={columns}

              pagination={false}

            />

          </Card>

          <Card title="Add observability connector">

            <Form form={form} layout="vertical" onFinish={handleCreate}>

              <Form.Item label="Type">

                <Select

                  value={selectedType}

                  onChange={setSelectedType}

                  options={datasourceTypes.map((t) => ({ value: t.type, label: t.label }))}

                />

              </Form.Item>

              <Form.Item name="name" label="Name" rules={[{ required: true }]}>

                <Input placeholder={selectedType === 'grafana' ? 'Prod Grafana' : 'Production Loki'} />

              </Form.Item>

              {renderTypeFields()}

              {renderCredentialFields()}

              <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>

                <Switch />

              </Form.Item>

              <Form.Item name="is_default" label="Default" valuePropName="checked">

                <Switch />

              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Add connector</Button>

            </Form>

          </Card>

        </TabPane>



        <TabPane tab="AI Assistant (MCP)" key="mcp">

          <Card

            title="MCP servers for chatbot tools"

            extra={

              <Button loading={importing} onClick={handleImportEnvMcp}>

                Import local MCP URLs

              </Button>

            }

            style={{ marginBottom: 16 }}

          >

            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>

              Register streamable-HTTP MCP servers (Prometheus, Loki, Billing). Use Test to verify tool discovery.

              Client: <code>{observabilityClientId}</code>

            </Text>

            <Table

              rowKey="connector_id"

              loading={loading}

              dataSource={mcpInstances}

              columns={mcpColumns}

              pagination={false}

            />

          </Card>

          <Card title="Add MCP server">

            <Form

              form={mcpForm}

              layout="vertical"

              onFinish={(values) => handleCreate(values, 'mcp')}

              initialValues={{ transport: 'streamable-http', model_affinity: 'all', enabled: true, agent_enabled: true }}

            >

              <Form.Item name="name" label="Name" rules={[{ required: true }]}>

                <Input placeholder="Local Prometheus MCP" />

              </Form.Item>

              {renderTypeFields('mcp')}

              {renderCredentialFields('mcp')}

              <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>

                <Switch />

              </Form.Item>

              <Form.Item name="agent_enabled" label="Expose to AI Assistant" valuePropName="checked" initialValue>

                <Switch />

              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Add MCP server</Button>

            </Form>

          </Card>

        </TabPane>



        <TabPane tab="Incident actions" key="incident">

          <Card title="Configured connectors" style={{ marginBottom: 16 }}>

            <Table

              rowKey="connector_id"

              loading={loading}

              dataSource={instances.filter((i) => i.category === 'action')}

              columns={columns}

              pagination={false}

            />

          </Card>

          <Card title="Add incident connector">

            <Form form={form} layout="vertical" onFinish={handleCreate}>

              <Form.Item label="Type">

                <Select

                  value={selectedType}

                  onChange={setSelectedType}

                  options={actionTypes.map((t) => ({ value: t.type, label: t.label }))}

                />

              </Form.Item>

              <Form.Item name="name" label="Name" rules={[{ required: true }]}>

                <Input />

              </Form.Item>

              {renderTypeFields()}

              {renderCredentialFields()}

              <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>

                <Switch />

              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Add connector</Button>

            </Form>

          </Card>



          <Card title="GitHub repo mappings" style={{ marginTop: 16 }}>

            <Table

              rowKey="rule_id"

              dataSource={repoMappings}

              pagination={false}

              columns={[

                { title: 'Label key', dataIndex: 'label_key' },

                { title: 'Label value', dataIndex: 'label_value' },

                { title: 'Owner', dataIndex: 'owner' },

                { title: 'Repo', dataIndex: 'repo' },

                {

                  title: '',

                  render: (_, r) => (

                    <Button

                      size="small"

                      danger

                      onClick={async () => {

                        await connectorsService.deleteRepoMapping(r.rule_id, INCIDENT_CLIENT_ID);

                        load();

                      }}

                    >

                      Delete

                    </Button>

                  ),

                },

              ]}

            />

            <Form form={mappingForm} layout="inline" onFinish={handleMapping} style={{ marginTop: 16 }}>

              <Form.Item name="label_key" rules={[{ required: true }]}><Input placeholder="service" /></Form.Item>

              <Form.Item name="label_value" rules={[{ required: true }]}><Input placeholder="backend" /></Form.Item>

              <Form.Item name="owner" rules={[{ required: true }]}><Input placeholder="org" /></Form.Item>

              <Form.Item name="repo" rules={[{ required: true }]}><Input placeholder="repo" /></Form.Item>

              <Form.Item><Button type="primary" htmlType="submit">Add mapping</Button></Form.Item>

            </Form>

          </Card>

        </TabPane>

      </Tabs>



      <Modal

        title={editingRecord ? `Edit connector — ${editingRecord.name}` : 'Edit connector'}

        open={editModalOpen}

        onCancel={closeEditModal}

        footer={null}

        destroyOnClose

        width={560}

      >

        {editingRecord && (

          <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>

            <Form.Item label="Type">

              <Input value={editingRecord.type} disabled />

            </Form.Item>

            <Form.Item name="name" label="Name" rules={[{ required: true }]}>

              <Input />

            </Form.Item>

            {renderTypeFields(editingRecord.type, { formInstance: editForm })}

            {renderCredentialFields(editingRecord.type, { editMode: true, formInstance: editForm })}

            {editingRecord.has_credentials && (

              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>

                Credentials are stored securely. Re-enter only to rotate or change them.

              </Text>

            )}

            <Form.Item name="enabled" label="Enabled" valuePropName="checked">

              <Switch />

            </Form.Item>

            {editingRecord.category === 'datasource' && (

              <Form.Item name="is_default" label="Default" valuePropName="checked">

                <Switch />

              </Form.Item>

            )}

            {editingRecord.type === 'mcp' && (

              <Form.Item name="agent_enabled" label="Expose to AI Assistant" valuePropName="checked">

                <Switch />

              </Form.Item>

            )}

            <Space>

              <Button type="primary" htmlType="submit" loading={editSaving}>

                Save changes

              </Button>

              <Button onClick={closeEditModal}>Cancel</Button>

            </Space>

          </Form>

        )}

      </Modal>

    </div>

  );

};



export default ConnectorsPage;


